import { CdkOverlayOrigin, CdkConnectedOverlay } from '@angular/cdk/overlay';
import { Component, Input, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { resetHubspotCookiesAndReloadPage } from '../../../../lib/hubspot';
import { AuthService } from '../../../auth/auth.service';
import { SnackbarService } from '../../../core/services/snackbar.service';
import { UserService } from '../../../core/services/user.service';
import { WINDOW } from '../../../core/window/window.token';
import { CompanyShort } from '../../../models/company';
import { DropdownOption } from '../../../models/dropdown-option';
import { ID } from '../../../models/id';
import { User } from '../../../models/user';
import { FormFieldSelectComponent } from '../../../ui-kit/form-field-select/form-field-select.component';
import { SpinnerComponent } from '../../../ui-kit/spinner/spinner.component';
import { SidebarButtonComponent } from '../sidebar-button/sidebar-button.component';

@Component({
  selector: 'app-sidebar-account-menu',
  templateUrl: './sidebar-account-menu.component.html',
  styleUrls: ['./sidebar-account-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SidebarButtonComponent,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    FormFieldSelectComponent,
    SpinnerComponent,
    MatButtonModule,
    RouterLink,
  ],
})
export class SidebarAccountMenuComponent implements OnInit {
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private router = inject(Router);
  private snackbarService = inject(SnackbarService);
  private window = inject<Window>(WINDOW);

  isAccountMenuOpen = false;
  user: User;
  companies: CompanyShort[] = [];
  companiesOptions: DropdownOption[] = [];
  loading: boolean;
  companiesControl: FormControl = new FormControl<ID>(null);
  @Input() selectedSideMenuName: string;

  ngOnInit(): void {
    this.user = this.userService.getUser();
    this.getUserCompanies();
    this.watchCompaniesValueChanges();
  }

  private watchCompaniesValueChanges(): void {
    this.companiesControl.valueChanges.subscribe((companyId) => {
      if (companyId !== this.user.company_id) {
        const selectedCompany = this.companies.find((company) => company.id === companyId);
        this.onSelectCompany(selectedCompany);
      } else {
        this.router.navigate(['/user', 'users-and-roles']);
      }
    });
  }

  private onSelectCompany(company: CompanyShort): void {
    this.loading = true;
    this.authService
      .selectCompany(company.id, false)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.toggleAccountMenu();
          this.window.location.assign('/homepage');
        },
        error: this.snackbarService.getHandleHttpErrorCallback(),
      });
  }

  toggleAccountMenu(): void {
    this.isAccountMenuOpen = !this.isAccountMenuOpen;
  }

  logout(): void {
    this.authService.logout().subscribe(() => {
      resetHubspotCookiesAndReloadPage();
    });
  }

  getUserCompanies(): void {
    this.authService.getUserCompanies().subscribe((companies) => {
      this.companies = companies;
      this.companiesOptions = companies.map((company) => ({
        value: company.id,
        viewValue: company.display_name,
      }));
      this.companiesControl.setValue(this.user.company_id, { emitEvent: false });
    });
  }

  get accountEmail(): string {
    return this.companies[0]?.contact_email || this.user?.email || '';
  }
}
